import React, { useCallback } from 'react';
import { useSnackbar } from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import { CreatorActionModal } from '~/components/CreatorActionModal/CreatorActionModal';
import { useCreatorActionModal } from '~/components/CreatorActionModal/CreatorActionModal.context';
import { queryClient } from '~/config/reactQueryConfig';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { useUpdateSaveListCreators } from '~/hooks/api/useSavedLists';
import {
  AGENCY_SAVED_LISTS,
  SAVED_LISTS,
  SAVED_LIST_CREATORS,
  SAVED_LIST_CREATORS_SEARCH,
} from '~/queries/keys';
import { useSavedListsStore } from '~/screens/MyAgency/store';

type ActionsProps = {
  list?: SavedList;
};

export const Actions: React.FC<ActionsProps> = ({ list }) => {
  const { creators: checked } = useSavedListsStore();
  const { creators: creatorsToAction, reset } = useCreatorActionModal();
  const { mutateAsync: mutateAsyncCreators } = useUpdateSaveListCreators();
  const snackbar = useSnackbar();

  const handleAddToListSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [AGENCY_SAVED_LISTS] });
    queryClient.invalidateQueries({
      queryKey: [SAVED_LIST_CREATORS],
    });
    queryClient.invalidateQueries({ queryKey: [SAVED_LISTS] });
    queryClient.invalidateQueries({
      queryKey: [SAVED_LIST_CREATORS_SEARCH],
    });
  }, []);

  const handleEditNoteSuccess = useCallback(() => {
    reset();
  }, [reset]);

  const handleRemoveFromList = useCallback(async () => {
    if (!creatorsToAction.length) {
      return;
    }

    const [{ id: creatorId }] = creatorsToAction;

    if (!creatorId || !list) {
      return;
    }

    const previousQueryData = queryClient.getQueryData([
      SAVED_LIST_CREATORS,
      list.id,
    ]);

    try {
      await mutateAsyncCreators({
        id: list.id,
        removeCreatorIds: [creatorId],
      });

      reset();

      queryClient.invalidateQueries({
        queryKey: [SAVED_LISTS],
      });

      snackbar.success({
        description: 'Talent successfully removed',
      });
    } catch (error) {
      queryClient.setQueryData(
        [SAVED_LIST_CREATORS, list.id],
        previousQueryData,
      );

      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  }, [list, creatorsToAction, mutateAsyncCreators, reset, snackbar]);

  return (
    <CreatorActionModal
      actions={{
        addToList: {
          creators: creatorsToAction.length ? creatorsToAction : checked,
          onSuccess: handleAddToListSuccess,
        },
        editNote: {
          creator: creatorsToAction[0],
          listId: list?.id,
          onSuccess: handleEditNoteSuccess,
        },
        removeFromList: {
          onRemove: handleRemoveFromList,
        },
      }}
    />
  );
};
